import { FC } from 'react';
import {
  useAppContext,
  useGetSurveyResponses,
  useUpdateSurveyResponses,
} from '@frx/frx-react-context';
import { Heading, useToast, VStack } from '@chakra-ui/react';
import { useEventId } from '../../../../hooks';
import {
  Card,
  Loader,
  Notification,
  QuestionsForm,
} from '../../../../components';
import { getReadOnlyQuestions, isMemphisMarathon } from '../../../../trConfig';

export const RegistrationDetails: FC = () => {
  const eventId = useEventId();
  const isSjmmwEditableEvent = isMemphisMarathon(eventId);
  const toast = useToast();
  const {
    data: {
      eventData: { eventConfig },
    },
  } = useAppContext();
  const { data: registrationQuestions, isInitialLoading } =
    useGetSurveyResponses({
      use_filters: true,
    });

  const QKEY_CPA_AGE_DATA = registrationQuestions?.responses.find(
    (q) => q.key === 'QKEY_CPA_AGE'
  );

  const GET_CPA_AGE_VALUE = () => {
    const responseValue = QKEY_CPA_AGE_DATA?.responseValue;

    if (typeof responseValue === 'string') {
      return responseValue;
    }

    return 'User Provided No Response';
  };

  const {
    mutate: updateRegistrationQuestions,
    isLoading: mutationLoading,
    isError,
    error,
  } = useUpdateSurveyResponses();

  const readOnlyQuestions = getReadOnlyQuestions(
    registrationQuestions?.responses || [],
    eventId
  );

  const questions =
    registrationQuestions?.responses.map((regQuestion) => ({
      ...regQuestion,
      isReadOnly: readOnlyQuestions.includes(regQuestion.key),
    })) || [];

  const handleSaveChanges = (formData: any) => {
    // CPA age verification question is hidden when editing registration.
    // However, we still need to post the original user response provided at registration time,
    // even though it is hidden for editing.The format of post data is question_999999

    const newFormData = {
      ...formData,
      ...(QKEY_CPA_AGE_DATA && {
        [`question_${QKEY_CPA_AGE_DATA?.questionId}`]: GET_CPA_AGE_VALUE(),
      }),
    };

    updateRegistrationQuestions(
      {
        ...newFormData,
        fr_id: eventId,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Changes Saved',
            status: 'success',
            duration: 2000,
          });
        },
      }
    );
  };

  return (
    <Card aria-label="Event Registration Details Card">
      <Heading size="md" color="brand.primary.swatch1">
        Event Registration Details
      </Heading>
      <VStack pt={8} spacing={6} align="stretch">
        {isInitialLoading && <Loader />}
        {isError && (
          <Notification message={error?.message as string} status="error" />
        )}
        <QuestionsForm
          trackingLabel="editRegSurvey"
          questions={questions}
          onSubmit={handleSaveChanges}
          hasSubmit={
            eventConfig?.personalSurveyEditAllowed || isSjmmwEditableEvent
          }
          buttonText="Save Changes"
          submitButtonProps={{
            width: 'full',
            isLoading: mutationLoading,
          }}
        />
      </VStack>
    </Card>
  );
};
